import { useContext, useEffect, useRef, useState } from 'react';
import {
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  Grid,
  Icon,
  LinearProgress,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { Archive, QrCode, Unarchive } from '@mui/icons-material';
import { toastError } from 'utils/toast';
import { useModal } from 'components/Modals';
import { EstoqueContext } from 'contexts/EstoqueContext';
import { ProdutosContext } from 'contexts/ProdutosContext';
import DeleteModal from 'components/Modals/DeleteModal';
import Container from 'components/Container';
import Button from 'components/Button';
import Header from 'components/Header';
import Input from 'components/Input';
import Card from 'components/Card';
import localeText from 'utils/localeText';
import styles from './styles';
import moment from 'moment';

const DetalheModal = ({ item }) => {
  const { closeModal } = useModal();
  const [tab, setTab] = useState(0);

  const Entradas = item?.Entradas || [];
  const Saidas = item?.Saidas || [];
  const Itens = tab === 0 ? Saidas : tab === 1 ? Entradas : [];

  return (
    <>
      <DialogTitle>Detalhes da Transferência</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Tabs value={tab} onChange={(_, t) => setTab(t)}>
              <Tab value={0} label="Saídas" />
              <Tab value={1} label="Entradas" />
            </Tabs>
          </Grid>
          <Grid item xs={12}>
            <DataGrid
              rows={Itens}
              columns={[
                {
                  field: 'rastreabilidade',
                  headerName: 'Rastreabilidade',
                  width: 200,
                  sortable: false,
                },
                {
                  field: 'variacao',
                  headerName: 'Variação',
                  width: 200,
                  sortable: false,
                },
                {
                  field: 'validade',
                  headerName: 'Validade',
                  width: 200,
                  sortable: false,
                  valueGetter: ({ value }) =>
                    value ? moment(value, 'YYYY-MM-DD').toDate() : null,
                  renderCell: ({ value }) => (
                    <Typography variant="body2">
                      {value
                        ? moment(value, 'YYYY-MM-DD')?.format('DD/MM/YYYY')
                        : ''}
                    </Typography>
                  ),
                },
                {
                  field: 'endereco',
                  headerName: 'Endereço',
                  flex: 1,
                  minWidth: 300,
                  sortable: false,
                  renderCell: ({ row }) => (
                    <Typography variant="body2" fontWeight="bold">
                      {row?.endereco}
                      {row?.endereco1 ? ` -> ${row?.endereco1}` : ''}
                      {row?.endereco2 ? ` -> ${row?.endereco2}` : ''}
                    </Typography>
                  ),
                },
                {
                  field: 'quantidade',
                  headerName: 'Quantidade',
                  type: 'number',
                  width: 200,
                  sortable: false,
                  renderCell: ({ row }) => (
                    <Typography>
                      {row?.quantidade} {item?.unidade_medida || ''}
                    </Typography>
                  ),
                },
              ]}
              hideFooter
              autoHeight
              density="compact"
              disableSelectionOnClick
              getRowHeight={() => 'auto'}
              getRowId={(item) => JSON.stringify(item)}
              disableColumnMenu
              showCellRightBorder
              showColumnRightBorder
              localeText={localeText}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button color="secondary" variant="contained" onClick={closeModal}>
          Voltar
        </Button>
      </DialogActions>
    </>
  );
};

const Transferencia = () => {
  const inputRef = useRef(null);
  const { openModal, closeModal } = useModal();
  const [itens, setItens] = useState([]);
  const [reader, setReader] = useState('');
  const [type, setType] = useState('RETIRADA');
  const [produtos, setProdutos] = useState([]);
  const [produto, setProduto] = useState(null);
  const [endereco, setEndereco] = useState(null);
  const [quantidade, setQuantidade] = useState('1');
  const [etqProduto, setEtqProduto] = useState(null);
  const [etqEndereco, setEtqEndereco] = useState(null);
  const { getLoading: prdLoading, getProduto } = useContext(ProdutosContext);
  const {
    getLoading,
    postLoading,
    deleteLoading,
    getTransferencia,
    postTransferencia,
    deleteTransferencia,
    getTransferenciaSaldo,
  } = useContext(EstoqueContext);

  const loading = getLoading || prdLoading || postLoading || deleteLoading;
  const shouldReadProduto =
    (type === 'RETIRADA' && Boolean(endereco)) ||
    (type === 'REPOSIÇÃO' && !Boolean(produto));

  const fetchData = () => {
    closeModal();
    setItens([]);
    setProduto(null);
    setEndereco(null);
    setQuantidade('1');
    setEtqProduto(null);
    setEtqEndereco(null);

    getTransferencia({ cb: setProdutos });
  };

  const handleBlur = () => {
    if (!Boolean(produto) || !Boolean(endereco)) {
      inputRef.current.focus();
    }
  };

  const onChangeText = (e) => setReader(e?.target?.value);

  const onChangeType = () => {
    setType((prev) => (prev === 'RETIRADA' ? 'REPOSIÇÃO' : 'RETIRADA'));
  };

  const validateEndereco = ({ endereco, itens = [], etiqueta = null }) => {
    if (!endereco)
      return toastError({ message: 'Endereço de estoque não encontrado' });
    if (endereco?.ativo !== 'SIM')
      return toastError({ message: 'Endereço de estoque inativo' });

    setItens(itens);
    setEndereco(endereco);
    setEtqEndereco(etiqueta);
  };

  const onReadEndereco = (etiqueta) => {
    const cb = (res = {}) => validateEndereco({ ...res, etiqueta });
    getTransferenciaSaldo({ params: { etiqueta }, cb });
  };

  const validateProduto = ({ produto = null, etiqueta = null }) => {
    if (!produto) return toastError({ message: 'Produto não encontrado' });
    if (produto?.ativo !== 'SIM')
      return toastError({ message: 'Produto inativo' });

    const isEqual = (a, b) => (a ?? null) === (b ?? null);
    let exists = false;

    if (type === 'RETIRADA') {
      exists = itens?.some(
        (s) =>
          isEqual(s?.produto_id, etiqueta?.produto_id) &&
          isEqual(s?.variacao_id, etiqueta?.variacao_id) &&
          isEqual(s?.rastreabilidade, etiqueta?.rastreabilidade)
      );
    } else {
      exists = produtos?.some(
        (s) =>
          isEqual(s?.produto_id, etiqueta?.produto_id) &&
          (s?.Saidas || []).some(
            (ss) =>
              isEqual(ss?.variacao_id, etiqueta?.variacao_id) &&
              isEqual(ss?.rastreabilidade, etiqueta?.rastreabilidade)
          )
      );
    }

    if (!exists)
      return toastError({
        message: `Produto indisponível para ${type?.toLowerCase()}`,
      });
    const descricao = produto?.ProdutoDescricaos?.find((f) =>
      ['COMPLETA', 'REDUZIDA']?.includes(f?.tipo)
    )?.descricao;
    const fullProd = { ...produto, descricao };
    setProduto(fullProd);
    setEtqProduto(etiqueta);
  };

  const onReadProduto = (etiqueta) => {
    const id = etiqueta?.produto_id || 0;
    const cb = (produto) => validateProduto({ produto, etiqueta });
    getProduto({ id, cb });
  };

  const onDeleteItem = (id) =>
    openModal(
      <DeleteModal
        onSubmit={() => deleteTransferencia({ id, cb: fetchData })}
      />
    );

  const handleSubmit = () => {
    const _quantidade =
      Number(quantidade || 0) * (type === 'RETIRADA' ? 1 : -1);
    const data = {
      endereco: etqEndereco,
      produto: etqProduto,
      quantidade: _quantidade,
    };

    postTransferencia({ data, cb: fetchData });
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!Boolean(produto) || !Boolean(endereco)) {
      inputRef.current.focus();
    }
  }, [produto, endereco]);

  useEffect(() => {
    if (!reader) return;

    const timeout = setTimeout(() => {
      let etiqueta = null;
      try {
        etiqueta = JSON.parse(reader);
      } catch (error) {}
      const isProduto = Boolean(etiqueta?.produto_id);
      const isEndereco = Boolean(etiqueta?.ceendereco_id);

      if (!etiqueta || (!isProduto && !isEndereco)) {
        toastError({ message: 'Erro na leitura da etiqueta' });
      }

      const handler = shouldReadProduto ? onReadProduto : onReadEndereco;
      handler(etiqueta);
      setReader('');
    }, 1000);

    return () => clearTimeout(timeout);
  }, [reader]);

  return (
    <Container>
      <Header titulo="Transferência" />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card>
            <Box display="flex" alignItems="center" gap={5}>
              <Box>
                <QrCode sx={styles?.qrcode} color="primary" />
                <Typography
                  variant="subtitle2"
                  flex={1}
                  textAlign="center"
                  color="primary"
                >
                  {shouldReadProduto ? 'PRODUTO' : 'ENDEREÇO'}
                </Typography>
              </Box>
              <Box flex={1} gap={1} display="flex" flexDirection="column">
                <Typography variant="h6" flex={1}>
                  Você está fazendo a {type}
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                  <Typography variant="caption" fontWeight="bold">
                    Endereço:
                  </Typography>
                  <Typography variant="caption">
                    {endereco?.endereco || '-'}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={2}>
                  <Typography variant="caption" fontWeight="bold">
                    Produto:
                  </Typography>
                  <Typography variant="caption">
                    {produto?.descricao || '-'}
                  </Typography>
                </Box>
                {Boolean(produto) && Boolean(endereco) && (
                  <Box alignSelf="start" my={2}>
                    <Input
                      type="number"
                      label="Quantidade"
                      value={quantidade}
                      onChange={(event) => setQuantidade(event.target.value)}
                      autoFocus
                    />
                  </Box>
                )}
                <Box alignSelf="start" mt={2} display="flex" gap={2}>
                  {Boolean(produto) && Boolean(endereco) && (
                    <Button
                      color="primary"
                      variant="contained"
                      onClick={handleSubmit}
                    >
                      Salvar
                    </Button>
                  )}
                  <Button
                    color="secondary"
                    variant="contained"
                    onClick={fetchData}
                  >
                    Cancelar
                  </Button>
                </Box>
              </Box>
              <Fab size="small" color="primary" onClick={onChangeType}>
                {type === 'RETIRADA' ? <Archive /> : <Unarchive />}
              </Fab>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <DataGrid
            rows={produtos}
            columns={[
              {
                field: 'referencia',
                headerName: 'Referência',
                width: 150,
                sortable: false,
              },
              {
                field: 'descricao',
                headerName: 'Produto',
                flex: 1,
                minWidth: 300,
                sortable: false,
              },
              {
                field: 'quantidade',
                headerName: 'Quantidade',
                type: 'number',
                width: 200,
                sortable: false,
                renderCell: ({ row }) => {
                  const Entradas = row?.Entradas || [];
                  const Saidas = row?.Saidas || [];
                  const quantidade_entrada =
                    Entradas?.reduce(
                      (acc, r) => acc + (r?.quantidade || 0),
                      0
                    ) || 0;
                  const quantidade_saida =
                    Saidas?.reduce((acc, r) => acc + (r?.quantidade || 0), 0) ||
                    0;
                  return (
                    <Typography>
                      {quantidade_entrada}/{quantidade_saida}{' '}
                      {row?.unidade_medida || ''}
                    </Typography>
                  );
                },
              },
              {
                field: 'actions',
                headerName: 'Ações',
                type: 'actions',
                width: 100,
                getActions: ({ row }) => [
                  <GridActionsCellItem
                    icon={<Icon>visibility</Icon>}
                    label="Detalhes"
                    onClick={() => openModal(<DetalheModal item={row} />, 90)}
                  />,
                  <GridActionsCellItem
                    icon={<Icon>delete</Icon>}
                    label="Excluir"
                    onClick={() => onDeleteItem(row?.id)}
                  />,
                ],
              },
            ]}
            hideFooter
            autoHeight
            density="compact"
            disableSelectionOnClick
            getRowHeight={() => 'auto'}
            disableColumnMenu
            showCellRightBorder
            showColumnRightBorder
            localeText={localeText}
            loading={loading}
            components={{ LoadingOverlay: LinearProgress }}
          />
        </Grid>
      </Grid>
      <input
        autoFocus
        ref={inputRef}
        value={reader}
        onChange={onChangeText}
        style={styles?.hideInput}
        onBlur={handleBlur}
      />
    </Container>
  );
};
export default Transferencia;
